"use client";

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardIcon,
} from "lucide-react";
import {
  useCallback,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "../utils";

// ---------------------------------------------------------------------------
// Variant palette
//
// Named variants let callers pick a colour theme without scattering
// `isUser ? "..." : "..."` conditionals across every section component.
//
// "default"  — neutral zinc (Vibes-style, also used as the baseline)
// "user"     — blue bubble (user messages in the playground)
// "tool"     — amber (tool-response messages)
// "error"    — red (error states)
// ---------------------------------------------------------------------------

export type CollapsibleVariant = "default" | "user" | "tool" | "error";

interface VariantStyles {
  container: string;
  headerText: string;
  headerHover: string;
  headerBorder: string;
  chevron: string;
  previewText: string;
  bodyText: string;
  copyButton: string;
}

const VARIANT_STYLES: Record<CollapsibleVariant, VariantStyles> = {
  default: {
    container: "border-zinc-200 bg-zinc-50/50",
    headerText: "text-zinc-600",
    headerHover: "hover:bg-zinc-100",
    headerBorder: "border-b border-zinc-200",
    chevron: "text-zinc-400",
    previewText: "text-zinc-500",
    bodyText: "text-zinc-700",
    copyButton: "bg-white/70",
  },
  user: {
    container: "border-blue-400/50 bg-blue-400/20",
    headerText: "text-blue-100",
    headerHover: "hover:bg-blue-400/30",
    headerBorder: "border-b border-blue-400/50",
    chevron: "text-blue-200",
    previewText: "text-blue-100",
    bodyText: "text-white",
    copyButton: "bg-blue-400/50",
  },
  tool: {
    container: "border-amber-200 bg-amber-100/50",
    headerText: "text-amber-700",
    headerHover: "hover:bg-amber-100",
    headerBorder: "border-b border-amber-200",
    chevron: "text-amber-500",
    previewText: "text-amber-600/80",
    bodyText: "text-zinc-700",
    copyButton: "bg-white/70",
  },
  error: {
    container: "border-red-200 bg-red-50",
    headerText: "text-red-700",
    headerHover: "hover:bg-red-100",
    headerBorder: "border-b border-red-200",
    chevron: "text-red-400",
    previewText: "text-red-600",
    bodyText: "text-red-700",
    copyButton:
      "bg-red-400/80 text-red-700 hover:bg-red-400/80 hover:text-red-800",
  },
};

/** Streaming shimmer gradient applied to the header label. */
const SHIMMER_STYLE: CSSProperties = {
  background:
    "linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #f3f4f6 50%, #d1d5db 75%, #9ca3af 100%)",
  backgroundSize: "200% 100%",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

/**
 * Convenience helper: resolves the variant from a boolean `isUser` flag.
 * Used by section components (ThinkingSection, CodeSection, etc.) that
 * receive `isUser` as a prop rather than a fully-typed variant string.
 */
export const resolveVariant = (isUser: boolean): CollapsibleVariant =>
  isUser ? "user" : "default";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CollapsibleSectionProps {
  /** Icon rendered before the label (e.g. <BrainIcon className="h-3 w-3" />). */
  icon: ReactNode;
  /** Short label shown in the header bar. */
  label: string;
  /** Optional secondary node appended after the label (e.g. a tool-call ID badge). */
  headerExtra?: ReactNode;
  /** The full body content shown when expanded. */
  children: ReactNode;

  /**
   * Collapsed preview content.
   *
   * - Pass a `string` for the simple Vibes-style preview (rendered as
   *   truncated text in the default zinc style).
   * - Pass a `ReactNode` for full control over preview rendering
   *   (ChatWidget-style `<pre>` / `<p>` previews with variant colours).
   */
  preview?: ReactNode;

  /**
   * Text handed to the copy button.
   * Both spellings are accepted so callers don't need to rename.
   */
  copyContent?: string;
  /** Alias for `copyContent` (ChatWidget convention). */
  copyText?: string;

  // ---- Controlled mode (ChatWidget-style) --------------------------------
  /** When provided together with `onToggle`, the component is fully controlled. */
  isExpanded?: boolean;
  /** Required when `isExpanded` is provided. */
  onToggle?: () => void;

  // ---- Uncontrolled mode (Vibes-style) -----------------------------------
  /** Initial open state when the component manages its own state. */
  defaultOpen?: boolean;

  // ---- Appearance --------------------------------------------------------
  /** Colour variant. Defaults to "default" (neutral zinc). */
  variant?: CollapsibleVariant;
  /**
   * Show the streaming shimmer animation on the header label, and render a
   * blue pulse dot to indicate live activity.
   */
  isStreaming?: boolean;
  /** Extra className on the outer wrapper. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CollapsibleSection = ({
  icon,
  label,
  headerExtra,
  children,
  preview,
  copyContent,
  copyText,
  isExpanded: isExpandedProp,
  onToggle,
  defaultOpen = false,
  variant = "default",
  isStreaming = false,
  className,
}: CollapsibleSectionProps) => {
  // Support both controlled (isExpanded + onToggle) and uncontrolled modes.
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = isExpandedProp !== undefined;
  const isOpen = isControlled ? isExpandedProp : internalOpen;

  const handleToggle = useCallback(() => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalOpen((prev) => !prev);
    }
  }, [isControlled, onToggle]);

  // Normalise the two copy-text prop names into one value.
  const copyValue = copyContent ?? copyText;

  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!copyValue) {return;}
      navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [copyValue],
  );

  const s = VARIANT_STYLES[variant];

  // In "default" variant render the Vibes-style ChevronRight trigger (rotates
  // 90° when open). All other variants use the ChevronDown (rotates 180°)
  // to match the playground's visual language.
  const isDefaultVariant = variant === "default";

  return (
    <div className={cn("rounded-md border", s.container, className)}>
      {/* ---- Header ---- */}
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        className={cn(
          "flex w-full items-center justify-between gap-2 px-2.5 py-1.5",
          "text-left text-xs transition-colors",
          s.headerText,
          s.headerHover,
          isOpen && s.headerBorder,
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400",
        )}
      >
        <div className="flex items-center gap-1.5 overflow-hidden">
          {isDefaultVariant ? (
            <ChevronRightIcon
              className={cn(
                "h-3 w-3 shrink-0 transition-transform duration-200",
                s.chevron,
                isOpen && "rotate-90",
              )}
            />
          ) : null}
          {icon}
          <span
            className={cn("font-medium", {
              "animate-shimmer-text": isStreaming,
            })}
            style={isStreaming ? SHIMMER_STYLE : undefined}
          >
            {label}
          </span>
          {headerExtra}
          {isStreaming && isDefaultVariant && (
            <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-blue-500" />
          )}
        </div>

        {/* Right side: copy button (default variant) or chevron (other variants) */}
        <div className="flex shrink-0 items-center gap-1">
          {copyValue && isDefaultVariant && (
            <div
              role="button"
              tabIndex={0}
              onClick={handleCopy}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCopy(e as unknown as React.MouseEvent);
                }
              }}
              aria-label={`Copy ${label}`}
              className={cn(
                "flex h-5 w-5 cursor-pointer items-center justify-center",
                "rounded-md",
                "opacity-0 transition-opacity duration-200",
                "hover:bg-zinc-100 hover:text-zinc-600",
                "group-hover/section:opacity-100",
                "focus-visible:opacity-100 focus-visible:outline-none",
              )}
            >
              {copied ? (
                <CheckIcon className="h-3 w-3 text-green-500" />
              ) : (
                <ClipboardIcon className="h-3 w-3" />
              )}
            </div>
          )}
          {!isDefaultVariant && (
            <ChevronDownIcon
              className={cn(
                "h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200",
                s.chevron,
                isOpen && "rotate-180",
              )}
            />
          )}
        </div>
      </button>

      {/* ---- Expanded body ---- */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "group/body relative min-w-0",
              isDefaultVariant ? "px-1.5 pb-1.5 pt-1" : "px-2.5 py-2",
            )}
          >
            {children}

            {/* Copy button inside expanded body for non-default variants */}
            {copyValue && !isDefaultVariant && (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={`Copy ${label}`}
                className={cn(
                  "absolute right-2 top-2 h-6 w-6",
                  "flex cursor-pointer items-center justify-center rounded-md",
                  "opacity-0 transition-opacity duration-200",
                  "group-hover/body:opacity-100",
                  "focus-visible:opacity-100 focus-visible:outline-none",
                  s.copyButton,
                )}
              >
                {copied ? (
                  <CheckIcon className="h-3 w-3 text-green-500" />
                ) : (
                  <ClipboardIcon className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---- Collapsed preview ---- */}
      {preview !== undefined && (
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            isOpen ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className={cn("px-2.5 py-1.5", isDefaultVariant && "px-1.5")}>
              {typeof preview === "string" ? (
                <span className={cn("truncate text-xs", s.previewText)}>
                  {preview}
                </span>
              ) : (
                preview
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
